import LoadingElement from "@/Components/Loading/LoadingElement";
import { Environment, QueryRenderer } from "react-relay";
import { graphql } from "relay-runtime";
import { relayAuthEnvironment } from "@/RelayEnvironment";
import ManagerDispositionMainView from "@/Domains/DispositionManagement/Views/ManagerDispositionMainView";
import { ManagerDispositionMainViewQueryRendererViewerQuery } from "@/Domains/DispositionManagement/QueryRenderer/__generated__/ManagerDispositionMainViewQueryRendererViewerQuery.graphql";
import LoginExpiredModal from "@/Components/LoginExpiredModal/LoginExpiredModal";
import React, {useState} from "react";
import {useAppSelector} from "@/Redux/Redux";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import {useSelector} from "react-redux";
import {selectLocale} from "@/Redux/App/AppReducer";
import TranslationGate from "@/Utils/TranslationGate";

interface ManagerComponentProps {
    isLoggedIn: boolean;
    width: number;
}

export default function ManagerDispositionMainViewQueryRenderer(
    props: ManagerComponentProps,
) {
    const { initialized, dispositionStartdate, dispositionEnddate } =
        useAppSelector((state) => state.disposition);

    const [tmpStartdate, setTmpStartdate] = useState<string | null>(null);
    const [tmpEnddate, setTmpEnddate] = useState<string | null>(null);
    const { prefixLoaded, addTranslations } = useTranslation();
    const language = useSelector(selectLocale);

    if (tmpStartdate != dispositionStartdate && tmpEnddate != dispositionEnddate && initialized) {
        setTmpStartdate(dispositionStartdate);
        setTmpEnddate(dispositionEnddate);
    }

    return (
        <>
            <QueryRenderer<ManagerDispositionMainViewQueryRendererViewerQuery>
                //@ts-ignore
                environment={relayAuthEnvironment() as Environment}
                query={graphql`
                    query ManagerDispositionMainViewQueryRendererViewerQuery($startdate: String, $enddate: String, $lang: String!, $prefixes: [String], $rightsFilter: [String]) {
                        viewer {
                            userId   
                            rights(filter: $rightsFilter) {
                                right
                            }                    
                        }
                        holidayList(startdate: $startdate, enddate: $enddate) {
                            holidays {
                                date
                                occasion                        
                            }                        
                        }
                        translationList(lang: $lang, prefixes: $prefixes) {
                                translations {
                                    component
                                    text
                                }
                            }
                        stationList {
                            stations {
                                stationId
                                city
                                country {
                                    countryId
                                    name
                                    sign
                                }
                                disponame
                                email
                                fax
                                isForeignStation
                                isOneWayStation
                                isRentStation
                                isSharingStation
                                latitude
                                longitude
                                name
                                openingdate
                                opentime {
                                    breakEndtime
                                    breakStarttime
                                    endtime
                                    opentimeId
                                    starttime
                                    station
                                    weekDay
                                }
                                phonenumber
                                street
                                zipcode
                                autoGenerateVehicleId
                            }
                        }
                        categoryList {
                        categories {
                            categoryId
                            sort
                            enabledVehicleGroups {
                                vehicleGroupId
                            }
                        }
                    }
                    colorList {
                        colors {
                            code
                            color
                            colorId
                            description
                            legendCrw
                            legendGroup
                            legendShorttext
                        }
                    }
                    equipmentList {
                        equipments {
                            equipmentId
                            description
                            showInDispo
                        }
                    }
                    legendList {
                        legends {
                            code
                            color
                            description
                            legendCrw
                            legendShorttext
                            legendGroup
                        }
                    }
                    regionList {
                        regions {
                            region
                            enabledStations {
                                stationId
                            }
                        }
                    }
                    vehicleGroupList {
                        vehicleGroups {
                            vehicleGroupId
                            category
                            categoryText
                            description
                            descriptionWeb
                            isEnabledForInternetUsage
                            sort
                            textWeb
                        }
                    }
                    }
                `}
                cacheConfig={{ force: true }}
                variables={{
                    startdate: tmpStartdate,
                    enddate: tmpEnddate,
                    lang: language?.locale ?? language,
                    prefixes: ["CRW6.disposition", "CRW6.bookingLine", "CRW6.vehicleLine", "CRW6.popover"],
                    rightsFilter: [
                        "VORG_SET_RES",
                        "VORG_SET_CO",
                        "VORG_VTC",
                        "KFZ_AENDERN"
                    ]
                }}
                render={(result) => {
                    if (result.error) {
                        return <LoginExpiredModal />;
                    }

                    if (!result.props?.viewer) {
                        return <LoadingElement />;
                    }

                    return (
                        <TranslationGate translationList={result.props?.translationList} prefix={"disposition"}>
                            <ManagerDispositionMainView
                                width={props.width}
                                viewData={result.props}
                                holidays={result.props?.holidayList?.holidays}
                            />
                        </TranslationGate>
                    );
                }}
            />
        </>
    );
}
